import { distinctUntilChanged, filter, interval, map, Observable } from 'rxjs';

interface GetCssColorOptions {
  readonly colorScheme?: 'dark' | 'light';
  readonly element?: HTMLElement;
}

const CHECK_INTERVAL = 50;
const MILLISECONDS_IN_SECOND = 1000;

export function getCssVar(name: string, element: HTMLElement = document.body): string {
  return getComputedStyle(element).getPropertyValue(`--${name}`).trim();
}

export function getCssVar$(name: string, element: HTMLElement = document.body): Observable<string> {
  return interval(CHECK_INTERVAL).pipe(
    map(() => getCssVar(name, element)),
    filter((v) => Boolean(v)),
    distinctUntilChanged(),
  );
}

// CSS color variables with the new approach contain the CSS function "light-dark" which will be returned as a value.
// To get the "actual" computed color based on the browser's "color-scheme" we need to use the new getCssColor function with a different approach.
// !!! Use it only if you need to pass the actual color to a place that does not support CSS variables (for example, canvas). !!!
// Value format: rgb(r, g, b), rgba(r, g, b, a)
export function getCssColor(name: string, options?: GetCssColorOptions): string {
  const span = document.createElement('span');

  span.style.color = `var(--${name})`;
  span.style.pointerEvents = 'none';
  span.style.position = 'fixed';
  span.style.visibility = 'hidden';

  if (options?.colorScheme) {
    span.style.colorScheme = options.colorScheme;
  }

  (options?.element ?? document.body).appendChild(span);

  try {
    return getComputedStyle(span).getPropertyValue('color');
  } finally {
    span.remove();
  }
}

export function getCssDuration(name: string): number {
  const value = getCssVar(name);
  const timeValue = parseFloat(value);

  if (value.endsWith('ms')) {
    return timeValue;
  }

  if (value.endsWith('s')) {
    return timeValue * MILLISECONDS_IN_SECOND;
  }

  throw new Error(`Not supported duration value is returned from ${name} CSS variable`);
}
